import { BrowserSessionApiClient } from "./browserSessionApiClient";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/+$/, "");

export type UserNotification = {
  userNotificationId: number;
  notificationType: string;
  title: string;
  message: string;
  actionUrl: string | null;
  entityType: string | null;
  entityId: number | null;
  sourceEvent: string | null;
  payload: Record<string, unknown> | null;
  isRead: boolean;
  readDate: string | null;
  createdDate: string | null;
};

export type UserNotificationListResult = {
  available: boolean;
  unreadCount: number;
  notifications: UserNotification[];
};

class UserNotificationService {
  private readonly browserClient = new BrowserSessionApiClient(API_BASE_URL);

  async list(limit = 12, excludeAssistant = false): Promise<UserNotificationListResult> {
    const response = await this.browserClient.request<{ data?: UserNotificationListResult } | UserNotificationListResult>(
      `${API_BASE_URL}/v1/notifications`,
      {
        method: "GET",
        query: { limit, ...(excludeAssistant ? { excludeAssistant: 1 } : {}) },
      }
    );

    const payload = response && typeof response === "object" && "data" in response
      ? response.data
      : response;

    return {
      available: Boolean(payload?.available ?? true),
      unreadCount: Number(payload?.unreadCount || 0) || 0,
      notifications: Array.isArray(payload?.notifications) ? payload.notifications : [],
    };
  }

  async markRead(notificationId: number) {
    const normalizedId = Math.trunc(Number(notificationId));
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
      throw new Error("A valid notificationId is required");
    }

    return this.browserClient.request(`${API_BASE_URL}/v1/notifications/${normalizedId}`, {
      method: "PATCH",
      body: { isRead: true },
    });
  }

  async markAllRead() {
    return this.browserClient.request(`${API_BASE_URL}/v1/notifications/read-all`, {
      method: "POST",
      body: {},
    });
  }
}

export const userNotificationService = new UserNotificationService();
